import { useEffect, useId, useRef, useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { loadSnap } from '@/lib/snap';

interface SnapEmbedProps {
  /** The Snap session token from the sale response. */
  token: string;
  /**
   * Snap reported something (paid, pending, failed). A HINT, never the
   * verdict: the browser can claim anything, so the panel only asks the server
   * to check — the sale settles on the gateway's word, server-side.
   */
  onGatewayEvent: () => void;
}

/**
 * Midtrans Snap, embedded where the QR would be drawn.
 *
 * Snap renders its own QR (QRIS only — the ticketbox asks for that when it
 * creates the sale) and its own countdown, so this component only mounts it
 * and reports back. Waiting, polling, expiry and retry stay with
 * `OnlineQrisPanel`, exactly as for a QR we draw ourselves.
 */
export function SnapEmbed({ token, onGatewayEvent }: SnapEmbedProps) {
  // `snap.embed` addresses its container by element id.
  const embedId = `snap-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // Latest callback without re-embedding: a fresh embed restarts Snap's page.
  const onEventRef = useRef(onGatewayEvent);
  onEventRef.current = onGatewayEvent;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setReady(false);
    loadSnap()
      .then((snap) => {
        if (cancelled) return;
        const container = document.getElementById(embedId);
        if (!container) return;
        // Snap holds ONE instance per page; clear any previous one first.
        try {
          snap.hide();
        } catch {
          /* nothing was open */
        }
        container.innerHTML = '';
        snap.embed(token, {
          embedId,
          onSuccess: () => onEventRef.current(),
          onPending: () => onEventRef.current(),
          onError: () => onEventRef.current(),
          // Closing Snap's page does not cancel the charge; the panel's own
          // buttons decide what happens next.
          onClose: () => onEventRef.current(),
        });
        setReady(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError((err as Error)?.message || 'Gagal membuka Snap Midtrans.');
      });
    return () => {
      cancelled = true;
      try {
        window.snap?.hide();
      } catch {
        /* already gone */
      }
    };
  }, [token, embedId]);

  if (error) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-destructive/30 bg-destructive/5 p-6 text-center">
        <TriangleAlert className="h-10 w-10 text-destructive" aria-hidden="true" />
        <p className="font-display text-base font-semibold text-destructive">
          Halaman pembayaran tidak dapat dibuka
        </p>
        <p className="text-sm text-ink-600">{error}</p>
      </div>
    );
  }

  return (
    // Same height bound as the QR it replaces (see OnlineQrisPanel), with a
    // floor: Snap's QR page is unusable below ~440px.
    <div className="relative h-[calc(100vh-23rem)] min-h-[440px] w-full overflow-hidden rounded-xl border-2 border-border bg-white">
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-ink-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}
      <div id={embedId} data-testid="snap-embed" className="h-full w-full" />
    </div>
  );
}
